/**
 * Cloudflare Email Service for YouthXConnect Events Admin System
 * 
 * This service provides a simple interface for sending emails through
 * the Cloudflare Worker that handles email delivery via Resend API.
 * 
 * Features:
 * - Simple API for approval/rejection emails
 * - Comprehensive error handling
 * - Professional logging
 * - Type validation
 * - Network resilience with retries
 * 
 * @author YouthXConnect Development Team
 * @version 2.0.0
 * @since 2024-01-01
 */

/**
 * Email Service Configuration
 * Centralized configuration for the email service
 */
const EMAIL_SERVICE_CONFIG = {
  // Cloudflare Pages Function endpoint - Update this with your deployed pages URL
  // For development: https://dev.youthxconnect-admin-panel.pages.dev/send-email
  // For production: https://youthxconnect-admin-panel.pages.dev/send-email
  // Or custom domain: https://yourdomain.com/send-email
  WORKER_URL: process.env.REACT_APP_EMAIL_WORKER_URL || 'https://youthxconnect-admin-panel.pages.dev/send-email',
  
  // Request timeout settings
  TIMEOUT_MS: 30000, // 30 seconds
  
  // Retry configuration for network failures
  RETRY: {
    MAX_ATTEMPTS: 2,
    INITIAL_DELAY_MS: 1000,
  }
};

/**
 * Custom Error Classes for Better Error Handling
 */
class EmailServiceError extends Error {
  constructor(message, code = 'UNKNOWN_ERROR', details = null) {
    super(message);
    this.name = 'EmailServiceError';
    this.code = code;
    this.details = details;
  }
}

class EmailValidationError extends EmailServiceError {
  constructor(message, details = null) {
    super(message, 'VALIDATION_ERROR', details);
    this.name = 'EmailValidationError';
  }
}

class EmailNetworkError extends EmailServiceError {
  constructor(message, details = null) {
    super(message, 'NETWORK_ERROR', details);
    this.name = 'EmailNetworkError';
  }
}

/**
 * Input Validation for Email Requests
 * Validates email request data before sending to worker
 * 
 * @param {string} recipientEmail - Email address of the recipient
 * @param {string} emailType - Type of email ('approval' or 'rejection')
 * @param {Object} registrationData - Registration information
 * @param {Object} eventData - Event information
 * @throws {EmailValidationError} - If validation fails
 */
function validateEmailRequest(recipientEmail, emailType, registrationData, eventData) {
  const errors = [];
  
  // Validate recipient email
  if (!recipientEmail || typeof recipientEmail !== 'string') {
    errors.push('Recipient email is required');
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail.trim())) {
      errors.push('Invalid email address format');
    }
  }
  
  // Validate email type
  if (!emailType || !['approval', 'rejection'].includes(emailType)) {
    errors.push('Email type must be either "approval" or "rejection"');
  }
  
  // Validate registration data
  if (!registrationData || typeof registrationData !== 'object') {
    errors.push('Registration data is required');
  } else {
    // Check for name in multiple possible locations based on the actual data structure
    const hasName = registrationData.personalInfo?.name || 
                   registrationData.personalInfo?.firstname || 
                   registrationData.personalInfo?.fullName ||
                   registrationData.name ||
                   registrationData.fullName;
    
    if (!hasName) {
      errors.push('Registrant name is required in registration data');
    }
  }
  
  // Validate event data
  if (!eventData || typeof eventData !== 'object') {
    errors.push('Event data is required');
  } else {
    if (!eventData.title) {
      errors.push('Event title is required in event data');
    }
  }
  
  if (errors.length > 0) {
    console.error('❌ Email validation failed:', {
      errors: errors,
      recipientEmail: recipientEmail,
      emailType: emailType,
      registrationData: registrationData,
      eventData: eventData
    });
    throw new EmailValidationError('Email request validation failed', errors);
  }
}

/**
 * Makes HTTP Request to Cloudflare Worker with Retry Logic
 * Handles network failures and implements retry mechanism
 * 
 * @param {Object} requestData - Data to send to the worker
 * @returns {Promise<Object>} - Response from the worker
 * @throws {EmailNetworkError} - If all network attempts fail
 */
async function makeWorkerRequest(requestData) {
  let lastError = null;
  
  for (let attempt = 1; attempt <= EMAIL_SERVICE_CONFIG.RETRY.MAX_ATTEMPTS; attempt++) {
    try {
      console.log(`📧 Email service attempt ${attempt}/${EMAIL_SERVICE_CONFIG.RETRY.MAX_ATTEMPTS}`);
      
      // Create AbortController for request timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), EMAIL_SERVICE_CONFIG.TIMEOUT_MS);
      
      // Make request to Cloudflare Worker
      const response = await fetch(EMAIL_SERVICE_CONFIG.WORKER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestData),
        signal: controller.signal,
      });
      
      // Clear timeout
      clearTimeout(timeoutId);
      
      // Parse response
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new EmailServiceError(
          responseData.error || 'Worker request failed',
          responseData.code || 'WORKER_ERROR',
          {
            status: response.status,
            statusText: response.statusText,
            details: responseData.details,
          }
        );
      }
      
      // Success - log and return
      console.log(`✅ Email sent successfully via worker:`, responseData);
      return responseData;
      
    } catch (error) {
      lastError = error;
      
      // Handle different types of errors
      if (error.name === 'AbortError') {
        console.error(`❌ Email attempt ${attempt} timed out`);
      } else if (error instanceof EmailServiceError) {
        console.error(`❌ Email attempt ${attempt} failed:`, error.message);
        // Don't retry on validation errors or worker errors
        throw error;
      } else {
        console.error(`❌ Email attempt ${attempt} network error:`, error.message);
      }
      
      // If this is not the last attempt, wait before retrying
      if (attempt < EMAIL_SERVICE_CONFIG.RETRY.MAX_ATTEMPTS) {
        const delay = EMAIL_SERVICE_CONFIG.RETRY.INITIAL_DELAY_MS * attempt;
        console.log(`⏳ Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // All attempts failed
  throw new EmailNetworkError(
    'Failed to communicate with email service after all attempts',
    {
      attempts: EMAIL_SERVICE_CONFIG.RETRY.MAX_ATTEMPTS,
      lastError: lastError?.message,
    }
  );
}

/**
 * Sends Registration Approval Email
 * Sends a professional approval email to the registrant
 * 
 * @param {string} recipientEmail - Email address of the registrant
 * @param {Object} registrationData - Registration information
 * @param {Object} eventData - Event information
 * @returns {Promise<Object>} - Result object with success status and details
 * 
 * @example
 * const result = await sendApprovalEmail(
 *   'user@example.com',
 *   { personalInfo: { name: 'John Doe' } },
 *   { title: 'Tech Conference 2024' }
 * );
 */
export async function sendApprovalEmail(recipientEmail, registrationData, eventData) {
  try {
    // Generate unique identifier for this email attempt to track duplicates
    const emailId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log(`📧 [${emailId}] Sending approval email to: ${recipientEmail}`);
    
    // Check if email service is properly configured
    const serviceStatus = getEmailServiceStatus();
    if (!serviceStatus.configured) {
      throw new EmailServiceError(
        `Email service not configured: ${serviceStatus.configurationIssue}`,
        'SERVICE_NOT_CONFIGURED',
        { workerUrl: serviceStatus.workerUrl }
      );
    }
    
    // Validate input parameters
    validateEmailRequest(recipientEmail, 'approval', registrationData, eventData);
    
    // Prepare request data for worker
    const requestData = {
      to: recipientEmail.trim(),
      type: 'approval',
      registrationData: registrationData,
      eventData: eventData,
    };
    
    // Send email via Cloudflare Worker
    const response = await makeWorkerRequest(requestData);
    
    console.log(`✅ [${emailId}] Approval email sent successfully to ${recipientEmail}`);
    
    return {
      success: true,
      message: 'Approval email sent successfully',
      data: response.data,
      emailId: emailId, // Include email ID in response for tracking
    };
    
  } catch (error) {
    console.error(`❌ Failed to send approval email to ${recipientEmail}:`, error);
    
    return {
      success: false,
      error: error.message,
      code: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    };
  }
}

/**
 * Sends Registration Rejection Email
 * Sends a professional rejection email to the registrant
 * 
 * @param {string} recipientEmail - Email address of the registrant
 * @param {Object} registrationData - Registration information
 * @param {Object} eventData - Event information
 * @param {string} rejectionReason - Optional reason for rejection
 * @returns {Promise<Object>} - Result object with success status and details
 * 
 * @example
 * const result = await sendRejectionEmail(
 *   'user@example.com',
 *   { personalInfo: { name: 'John Doe' } },
 *   { title: 'Tech Conference 2024' },
 *   'Application incomplete'
 * );
 */
export async function sendRejectionEmail(recipientEmail, registrationData, eventData, rejectionReason = null) {
  try {
    // Generate unique identifier for this email attempt to track duplicates
    const emailId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log(`📧 [${emailId}] Sending rejection email to: ${recipientEmail}`);
    
    // Check if email service is properly configured
    const serviceStatus = getEmailServiceStatus();
    if (!serviceStatus.configured) {
      throw new EmailServiceError(
        `Email service not configured: ${serviceStatus.configurationIssue}`,
        'SERVICE_NOT_CONFIGURED',
        { workerUrl: serviceStatus.workerUrl }
      );
    }
    
    // Validate input parameters
    validateEmailRequest(recipientEmail, 'rejection', registrationData, eventData);
    
    // Prepare request data for worker
    const requestData = {
      to: recipientEmail.trim(),
      type: 'rejection',
      registrationData: registrationData,
      eventData: eventData,
      rejectionReason: rejectionReason,
    };
    
    // Send email via Cloudflare Worker
    const response = await makeWorkerRequest(requestData);
    
    console.log(`✅ [${emailId}] Rejection email sent successfully to ${recipientEmail}`);
    
    return {
      success: true,
      message: 'Rejection email sent successfully',
      data: response.data,
      emailId: emailId, // Include email ID in response for tracking
    };
    
  } catch (error) {
    console.error(`❌ Failed to send rejection email to ${recipientEmail}:`, error);
    
    return {
      success: false,
      error: error.message,
      code: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    };
  }
}

/**
 * Gets Email Service Status
 * Checks if the email service is available and properly configured
 * 
 * @returns {Object} - Service status information
 */
export function getEmailServiceStatus() {
  const isConfigured = Boolean(EMAIL_SERVICE_CONFIG.WORKER_URL && 
                              !EMAIL_SERVICE_CONFIG.WORKER_URL.includes('your-subdomain') &&
                              !EMAIL_SERVICE_CONFIG.WORKER_URL.includes('undefined'));
  
  return {
    configured: isConfigured,
    workerUrl: EMAIL_SERVICE_CONFIG.WORKER_URL,
    timeout: EMAIL_SERVICE_CONFIG.TIMEOUT_MS,
    maxRetries: EMAIL_SERVICE_CONFIG.RETRY.MAX_ATTEMPTS,
    configurationIssue: !isConfigured ? 'REACT_APP_EMAIL_WORKER_URL not set or contains placeholder' : null,
  };
}

/**
 * Email Service Configuration Helper
 * Provides information about required environment variables
 * 
 * @returns {Object} - Configuration requirements and current status
 */
export function getEmailServiceConfig() {
  const status = getEmailServiceStatus();
  
  return {
    status: status,
    requirements: {
      environmentVariable: 'https://admin-panel-mailer.youthxconnect.workers.dev/',
      description: 'https://admin-panel-mailer.youthxconnect.workers.dev/',
      example: 'https://admin-panel-mailer.youthxconnect.workers.dev/',
    },
    setupInstructions: [
      '1. Deploy the Cloudflare Worker using: wrangler deploy',
      '2. Get the worker URL from the deployment output',
      '3. Set REACT_APP_EMAIL_WORKER_URL in your .env file',
      '4. Restart your development server',
    ],
  };
}

// Export error classes for error handling in components
export {
  EmailServiceError,
  EmailValidationError,
  EmailNetworkError,
}; 